import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../useAuth';
import api from '../utils/api';
import SEOHelmet from '../components/SEOHelmet';
import { trackBeginCheckout } from '../utils/analytics';

const FEATURES_FREE = [
  '1 profession, 1 state',
  '1 active compliance cycle',
  'Deadline email alerts',
  'PDF compliance report export',
  'Full category tracking (HSW, Ethics, etc.)',
];

const FEATURES_SOLO = [
  '1 profession, 1 state',
  'Unlimited compliance cycles',
  'Deadline email alerts',
  'PDF compliance report export',
  'Full category tracking',
  'Certificate storage',
];

const FEATURES_PRO = [
  'Unlimited professions & states',
  'Unlimited compliance cycles',
  'Deadline email alerts',
  'PDF compliance report export',
  'Full category tracking (HSW, Ethics, etc.)',
  'Priority email support',
];

const FEATURES_TEAM = [
  'Everything in Pro — per seat',
  'Company admin dashboard',
  'Firm-level compliance reporting',
  'Manage linked & managed licensees',
  'Add cycles & licenses on behalf of staff',
  'Bulk CSV import',
  'Team compliance report with PDF export',
];

interface TeamTier {
  label: string;
  seats: string;
  monthly: number;
  annual: number;
  annualSave: number;
  highlight?: boolean;
}

const TEAM_TIERS: TeamTier[] = [
  { label: 'Micro',   seats: 'Up to 5 members',  monthly: 30, annual: 24, annualSave: 72 },
  { label: 'Starter', seats: 'Up to 10 members', monthly: 40, annual: 36, annualSave: 48, highlight: true },
  { label: 'Firm',    seats: 'Up to 30 members', monthly: 50, annual: 48, annualSave: 24 },
];

const Pricing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interval, setInterval] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  // Collapsed by default — company/team pricing shouldn't compete for
  // attention on a page individual users land on first.
  const [showTeamPricing, setShowTeamPricing] = useState(false);

  const soloMonthly = 3;
  const soloAnnual  = 2;    // $24/yr shown as its monthly-equivalent
  const soloPrice   = interval === 'annual' ? soloAnnual : soloMonthly;

  const proMonthly = 5;
  const proAnnual  = 4;     // $48/yr shown as its monthly-equivalent
  const proPrice   = interval === 'annual' ? proAnnual : proMonthly;

  const currentPlan = user?.subscription?.plan ?? 'free';
  const isTrialing = user?.subscription?.status === 'trialing';
  const isSolo = currentPlan === 'solo' && !isTrialing;
  // A trialing user's plan is already 'pro', but they haven't actually paid —
  // treat them like a free user for CTA purposes so "Start Pro" stays clickable.
  const isPro  = currentPlan === 'pro' && !isTrialing;

  const handleSubscribe = async (plan: 'solo' | 'pro' | 'team', tierLabel?: string) => {
    trackBeginCheckout(plan, interval, tierLabel);
    if (!user) {
      const params = new URLSearchParams({ plan, interval });
      if (tierLabel) params.set('tier', tierLabel);
      navigate(`/register?${params.toString()}`);
      return;
    }
    const key = tierLabel ? `team-${tierLabel}` : plan;
    setLoading(key);
    setError('');
    try {
      const { data } = await api.post('/api/billing/create-checkout-session', { plan, interval, tier: tierLabel });
      if (data.activated) {
        navigate('/billing?success=true');
      } else {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <>
      <SEOHelmet
        title="Pricing — CEC Track"
        description="Simple, transparent pricing for CE compliance tracking. Free for individuals, Pro for power users, Team plans for firms of any size."
        canonical="https://cectrack.com/pricing"
      />

      <div style={{ background: 'var(--color-surface)', minHeight: '100vh' }}>

        {/* Hero */}
        <div className="text-center py-5" style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, #1a3a6e 100%)' }}>
          <div className="container">
            <div className="d-inline-block px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(255,255,255,0.15)', fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Simple Pricing
            </div>
            <h1 className="display-5 fw-bold text-white mb-3">
              Track Every Credit.<br />Renew With Confidence.
            </h1>
            <p className="text-white-50 mb-3" style={{ fontSize: '1.1rem', maxWidth: 560, margin: '0 auto 1rem' }}>
              Start with full Pro access — track every state license and renewal cycle you have, free for 60 days.
            </p>
            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4"
              style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b' }}
            >
              <span style={{ fontSize: '1.1rem' }}>🎉</span>
              <span className="fw-bold" style={{ color: '#f59e0b', fontSize: '0.9rem' }}>
                60-day Pro trial for every new account — no credit card required
              </span>
            </div>

            {/* Billing toggle */}
            <div className="d-inline-flex align-items-center gap-3 p-1 rounded-pill" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <button
                className={`btn btn-sm rounded-pill px-4 ${interval === 'monthly' ? 'btn-light' : 'text-white border-0'}`}
                onClick={() => setInterval('monthly')}
                style={{ fontWeight: 600 }}
              >
                Monthly
              </button>
              <button
                className={`btn btn-sm rounded-pill px-4 ${interval === 'annual' ? 'btn-light' : 'text-white border-0'}`}
                onClick={() => setInterval('annual')}
                style={{ fontWeight: 600 }}
              >
                Annual
                <span className="ms-2 badge" style={{ background: '#f59e0b', fontSize: '0.65rem' }}>Save up to 33%</span>
              </button>
            </div>
            {interval === 'annual' && (
              <p className="mt-3 mb-0" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                🛡️ Annual plans include a 14-day money-back guarantee.
              </p>
            )}
          </div>
        </div>

        <div className="container py-5">
          {error && <div className="alert alert-danger text-center mb-4">{error}</div>}

          {/* ── Individual Plans ── */}
          <h5 className="text-center fw-bold mb-4" style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '1rem', color: 'var(--color-primary)' }}>
            Individual Plans
          </h5>
          <div className="row g-4 justify-content-center mb-5">

            {/* Free */}
            <div className="col-12 col-md-4">
              <div className="card h-100 border shadow-sm">
                <div className="card-body p-4 d-flex flex-column">
                  <div className="mb-3">
                    <span className="badge bg-secondary mb-2">Free</span>
                    <div className="d-flex align-items-end gap-1 mb-1">
                      <span style={{ fontSize: '2.8rem', fontWeight: 800, lineHeight: 1 }}>$0</span>
                    </div>
                    <p className="text-muted small mb-0">Forever free — no credit card required</p>
                  </div>
                  <hr />
                  <ul className="list-unstyled flex-grow-1">
                    {FEATURES_FREE.map(f => (
                      <li key={f} className="d-flex align-items-start gap-2 mb-2">
                        <span className="text-success mt-1">✓</span>
                        <span className="small">{f}</span>
                      </li>
                    ))}
                  </ul>
                  {!user && (
                    <button
                      className="btn btn-outline-secondary w-100 mt-3"
                      onClick={() => navigate('/register')}
                    >
                      Get started free
                    </button>
                  )}
                  {user && currentPlan === 'free' && (
                    <button className="btn btn-outline-secondary w-100 mt-3" disabled>
                      Your current plan
                    </button>
                  )}
                  {user && isTrialing && (
                    <p className="text-center small text-muted mt-3 mb-0">
                      You're on a Pro trial — this is where you'll land after it ends.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Solo */}
            <div className="col-12 col-md-4">
              <div className="card h-100 border shadow-sm">
                <div className="card-body p-4 d-flex flex-column">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge" style={{ background: '#0d9488', color: '#fff' }}>Solo</span>
                      {interval === 'annual' && (
                        <span className="badge" style={{ background: '#d1fae5', color: '#065f46', fontSize: '0.7rem' }}>Save $12/yr</span>
                      )}
                    </div>
                    <div className="d-flex align-items-end gap-1 mb-1">
                      <span style={{ fontSize: '2.8rem', fontWeight: 800, lineHeight: 1 }}>${soloPrice}</span>
                      <span className="text-muted" style={{ marginBottom: '0.4rem' }}>/mo</span>
                    </div>
                    <p className="text-muted small mb-0">
                      {interval === 'annual'
                        ? 'Billed $24/year — save $12 vs. monthly'
                        : 'Billed $3/month — switch to annual to save $12/yr'}
                    </p>
                  </div>
                  <hr />
                  <ul className="list-unstyled flex-grow-1">
                    {FEATURES_SOLO.map(f => (
                      <li key={f} className="d-flex align-items-start gap-2 mb-2">
                        <span className="mt-1" style={{ color: '#0d9488' }}>✓</span>
                        <span className="small">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="d-flex rounded-2 overflow-hidden mt-3" style={{ border: '1px solid #dee2e6' }}>
                    <button
                      className="flex-fill btn btn-sm py-2"
                      style={{ borderRadius: 0, background: interval === 'monthly' ? '#0d9488' : '#f8f9fa', color: interval === 'monthly' ? '#fff' : '#495057', fontWeight: 600, border: 'none' }}
                      onClick={() => setInterval('monthly')}
                    >
                      Monthly — $3/mo
                    </button>
                    <button
                      className="flex-fill btn btn-sm py-2"
                      style={{ borderRadius: 0, background: interval === 'annual' ? '#0d9488' : '#f8f9fa', color: interval === 'annual' ? '#fff' : '#495057', fontWeight: 600, border: 'none', borderLeft: '1px solid #dee2e6' }}
                      onClick={() => setInterval('annual')}
                    >
                      Annual — $24/yr
                    </button>
                  </div>
                  {isSolo ? (
                    <button className="btn w-100 mt-2 fw-bold" disabled style={{ background: '#0d9488', color: '#fff', border: 'none', opacity: 0.7 }}>
                      ✓ Your current plan
                    </button>
                  ) : (
                    <button
                      className="btn w-100 mt-2 fw-bold"
                      style={{ background: '#0d9488', color: '#fff', border: 'none' }}
                      onClick={() => handleSubscribe('solo')}
                      disabled={loading === 'solo'}
                    >
                      {loading === 'solo' ? 'Redirecting…' : `Start Solo — ${interval === 'annual' ? '$24/yr' : '$3/mo'}`}
                    </button>
                  )}
                  <p className="text-center text-muted mt-2 mb-0" style={{ fontSize: '0.75rem' }}>
                    {isSolo ? '✓ Active subscription' : interval === 'annual' ? '14-day money-back guarantee' : 'Cancel anytime'}
                  </p>
                </div>
              </div>
            </div>

            {/* Pro */}
            <div className="col-12 col-md-4">
              <div className="card h-100 border-0 shadow-lg" style={{ background: 'var(--color-primary)', color: '#fff' }}>
                <div className="card-body p-4 d-flex flex-column">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge" style={{ background: '#f59e0b', color: '#000' }}>Pro — Most Popular</span>
                      {interval === 'annual' && (
                        <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.7rem' }}>Save $12/yr</span>
                      )}
                    </div>
                    <div className="d-flex align-items-end gap-1 mb-1">
                      <span style={{ fontSize: '2.8rem', fontWeight: 800, lineHeight: 1 }}>${proPrice}</span>
                      <span style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '0.4rem' }}>/mo</span>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: 0 }}>
                      {interval === 'annual'
                        ? 'Billed $48/year — save $12 vs. monthly'
                        : `Billed $${proMonthly}/month — switch to annual to save $12/yr`}
                    </p>
                  </div>
                  <hr style={{ borderColor: '#ffffff' }} />
                  <ul className="list-unstyled flex-grow-1">
                    {FEATURES_PRO.map(f => (
                      <li key={f} className="d-flex align-items-start gap-2 mb-2">
                        <span style={{ color: '#f59e0b' }} className="mt-1">✓</span>
                        <span className="small" style={{ color: 'rgba(255,255,255,0.9)' }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {/* Inline billing toggle */}
                  <div className="d-flex rounded-2 overflow-hidden mt-3" style={{ border: '1px solid rgba(255,255,255,0.3)' }}>
                    <button
                      className="flex-fill btn btn-sm py-2"
                      style={{ borderRadius: 0, background: interval === 'monthly' ? '#f59e0b' : 'transparent', color: interval === 'monthly' ? '#000' : 'rgba(255,255,255,0.8)', fontWeight: 600, border: 'none' }}
                      onClick={() => setInterval('monthly')}
                    >
                      Monthly — $5/mo
                    </button>
                    <button
                      className="flex-fill btn btn-sm py-2"
                      style={{ borderRadius: 0, background: interval === 'annual' ? '#f59e0b' : 'transparent', color: interval === 'annual' ? '#000' : 'rgba(255,255,255,0.8)', fontWeight: 600, border: 'none', borderLeft: '1px solid rgba(255,255,255,0.3)' }}
                      onClick={() => setInterval('annual')}
                    >
                      Annual — $48/yr
                    </button>
                  </div>
                  {isPro ? (
                    <button
                      className="btn w-100 mt-2 fw-bold"
                      disabled
                      style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}
                    >
                      ✓ Your current plan
                    </button>
                  ) : (
                    <button
                      className="btn w-100 mt-2 fw-bold"
                      style={{ background: '#f59e0b', color: '#000', border: 'none' }}
                      onClick={() => handleSubscribe('pro')}
                      disabled={loading === 'pro'}
                    >
                      {loading === 'pro' ? 'Redirecting…' : `Start Pro — ${interval === 'annual' ? '$48/yr' : '$5/mo'}`}
                    </button>
                  )}
                  <p className="text-center mt-2 mb-0" style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)' }}>
                    {isPro ? '✓ Active subscription' : interval === 'annual' ? '🛡️ 14-day money-back guarantee' : 'Cancel anytime'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Team Plans (collapsed by default) ── */}
          <div className="text-center mb-4">
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0"
              onClick={() => setShowTeamPricing(v => !v)}
              aria-expanded={showTeamPricing}
              style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '1rem', color: 'var(--color-primary)' }}
            >
              {showTeamPricing ? '▲' : '▾'} Team &amp; Firm Plans
            </button>
            <p className="text-muted small mb-0 mt-1">
              {showTeamPricing
                ? 'Centrally manage CE compliance for your entire staff — licenses, cycles, and compliance reports in one place.'
                : 'Managing CE compliance for a firm or multiple staff? Click to see Team & Firm pricing.'}
            </p>
          </div>

          {showTeamPricing && (
          <>
          <div className="row g-4 justify-content-center mb-4">
            {TEAM_TIERS.map(tier => {
              const price = interval === 'annual' ? tier.annual : tier.monthly;
              const saveLabel = `Save $${tier.annualSave}/yr`;
              const isLoading = loading === `team-${tier.label}`;
              return (
                <div key={tier.label} className="col-12 col-md-4">
                  <div
                    className="card h-100 shadow-sm"
                    style={{
                      border: tier.highlight ? '2px solid var(--color-primary)' : '1px solid #dee2e6',
                      transform: tier.highlight ? 'scale(1.02)' : undefined,
                      zIndex: tier.highlight ? 1 : undefined,
                    }}
                  >
                    <div className="card-body p-4 d-flex flex-column">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className={`badge ${tier.highlight ? 'bg-primary' : 'bg-secondary'}`}>
                            {tier.label}
                          </span>
                          {interval === 'annual' && (
                            <span className="badge" style={{ background: '#d1fae5', color: '#065f46', fontSize: '0.65rem' }}>
                              {saveLabel}
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-end gap-1 mb-1">
                          <span style={{ fontSize: '2.5rem', fontWeight: 800, lineHeight: 1 }}>${price}</span>
                          <span className="text-muted" style={{ marginBottom: '0.4rem' }}>/mo</span>
                        </div>
                        <p className="text-muted small mb-1">{tier.seats}</p>
                        {interval === 'annual' && (
                          <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                            Billed ${tier.annual * 12}/year
                          </p>
                        )}
                        {interval === 'monthly' && (
                          <p style={{ fontSize: '0.75rem', color: '#065f46', marginBottom: 0 }}>
                            Switch to annual — save ${tier.annualSave}/yr
                          </p>
                        )}
                      </div>
                      <hr />
                      <ul className="list-unstyled flex-grow-1">
                        {FEATURES_TEAM.map(f => (
                          <li key={f} className="d-flex align-items-start gap-2 mb-2">
                            <span className="text-primary mt-1">✓</span>
                            <span className="small">{f}</span>
                          </li>
                        ))}
                      </ul>
                      {/* Inline billing toggle */}
                      <div className="d-flex rounded-2 overflow-hidden mt-3" style={{ border: '1px solid #dee2e6' }}>
                        <button
                          className="flex-fill btn btn-sm py-2"
                          style={{ borderRadius: 0, background: interval === 'monthly' ? 'var(--color-primary)' : '#f8f9fa', color: interval === 'monthly' ? '#fff' : '#495057', fontWeight: 600, border: 'none' }}
                          onClick={() => setInterval('monthly')}
                        >
                          Monthly — ${tier.monthly}/mo
                        </button>
                        <button
                          className="flex-fill btn btn-sm py-2"
                          style={{ borderRadius: 0, background: interval === 'annual' ? 'var(--color-primary)' : '#f8f9fa', color: interval === 'annual' ? '#fff' : '#495057', fontWeight: 600, border: 'none', borderLeft: '1px solid #dee2e6' }}
                          onClick={() => setInterval('annual')}
                        >
                          Annual — ${tier.annual * 12}/yr
                        </button>
                      </div>
                      <button
                        className={`btn w-100 mt-2 ${tier.highlight ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleSubscribe('team', tier.label)}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Redirecting…' : `Start ${tier.label} — ${interval === 'annual' ? `$${tier.annual * 12}/yr` : `$${tier.monthly}/mo`}`}
                      </button>
                      <p className="text-center text-muted mt-2 mb-0" style={{ fontSize: '0.75rem' }}>
                        {interval === 'annual' ? '14-day money-back guarantee' : 'Cancel anytime'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Enterprise contact card */}
          <div
            className="rounded-3 p-4 text-center mb-5"
            style={{ background: 'linear-gradient(135deg, #0f1f3d08, #0f1f3d14)', border: '1px solid #0f1f3d22' }}
          >
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-3">
              <div className="text-start">
                <span className="badge bg-dark mb-2">Enterprise</span>
                <h5 className="fw-bold mb-1">30+ members</h5>
                <p className="text-muted small mb-0">
                  Large firms and multi-office organizations. Custom seat limits, onboarding support, and volume pricing.
                </p>
              </div>
              <div className="ms-md-5 flex-shrink-0">
                <a
                  href="/contact"
                  className="btn btn-dark px-4"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Contact Us →
                </a>
              </div>
            </div>
          </div>
          </>
          )}

          {/* Feature comparison table */}
          <div className="mt-2">
            <h4 className="text-center fw-bold mb-4">Everything You Get</h4>
            <div className="table-responsive">
              <table className="table table-bordered align-middle text-center">
                <thead className="table-dark">
                  <tr>
                    <th className="text-start" style={{ width: '30%' }}>Feature</th>
                    <th>Free</th>
                    <th style={{ background: '#0d9488', color: '#fff' }}>Solo</th>
                    <th style={{ background: 'var(--color-primary)', color: '#fff' }}>Pro</th>
                    <th>
                      Team
                      <div style={{ fontSize: '0.7rem', fontWeight: 400, opacity: 0.75, marginTop: 2 }}>
                        Starter · Firm · Practice
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Professions tracked',           '1',  '1',         'Unlimited', 'Unlimited'],
                    ['States tracked',                '1',  '1',         'Unlimited', 'Unlimited'],
                    ['Active cycles',                 '1',  'Unlimited', 'Unlimited', 'Unlimited'],
                    ['Category tracking (HSW, etc.)', '✓',  '✓',         '✓',         '✓'],
                    ['Deadline email alerts',         '—',  '✓',         '✓',         '✓'],
                    ['PDF compliance export',         '✓',  '✓',         '✓',         '✓'],
                    ['Certificate storage',           '—',  '✓',         '✓',         '✓'],
                    ['Company admin dashboard',       '—',  '—',         '—',         '✓'],
                    ['Manage staff licensees',        '—',  '—',         '—',         '✓'],
                    ['Firm compliance report',        '—',  '—',         '—',         '✓'],
                    ['Bulk CSV import',               '—',  '—',         '—',         '✓'],
                  ].map(([feature, free, solo, pro, team]) => (
                    <tr key={feature}>
                      <td className="text-start fw-medium">{feature}</td>
                      <td className={free === '—' ? 'text-muted' : ''}>{free}</td>
                      <td className={solo === '—' ? 'text-muted' : 'fw-semibold'} style={solo !== '—' ? { color: '#0d9488' } : {}}>{solo}</td>
                      <td className={pro === '—' ? 'text-muted' : 'text-success fw-semibold'}>{pro}</td>
                      <td className={team === '—' ? 'text-muted' : 'text-primary fw-semibold'}>{team}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-5 row justify-content-center">
            <div className="col-12 col-lg-8">
              <h4 className="text-center fw-bold mb-4">Common Questions</h4>
              {[
                {
                  q: 'Is the Free plan really free?',
                  a: 'Yes — no credit card required, no time limit. The Free plan gives you one profession, one state, and one active compliance cycle permanently. Upgrade whenever your needs grow.',
                },
                {
                  q: 'Can I cancel my subscription anytime?',
                  a: 'Yes. Cancel anytime from your Billing Settings — no need to call or email. You keep full access until the end of your current billing period, then your account reverts to the Free plan. You can reactivate at any time.',
                },
                {
                  q: 'What happens to my data if I downgrade to Free?',
                  a: "Your data is preserved. Extra cycles beyond the first become read-only and deadline email alerts are disabled, but you can still view your history and print compliance reports.",
                },
                {
                  q: 'How do Team plan tiers work?',
                  a: 'Starter covers up to 10 members, Firm up to 30, and Practice up to 100. All tiers include the same features — the only difference is seat count and price. If your firm grows past a tier, upgrade to the next one and your data carries over.',
                },
                {
                  q: 'What counts as a "member" on a Team plan?',
                  a: 'Any licensed professional added to your firm account counts toward your seat limit — both staff with their own login and profiles managed directly by the firm admin.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="mb-3 p-3 rounded border">
                  <strong>{q}</strong>
                  <p className="text-muted mb-0 mt-1 small">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
